import { Input } from '@angular/core'
import { Component } from '@angular/core'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faDownload } from '@fortawesome/free-solid-svg-icons'
import { CellType, FlexGrid } from '@grapecity/wijmo.grid'
import { FlexGridXlsxConverter } from '@grapecity/wijmo.grid.xlsx'
import { Workbook } from '@grapecity/wijmo.xlsx'

@Component({
    selector: 'grid-export',
    templateUrl: './grid-export.component.html',
    standalone: true,
    imports: [FaIconComponent],
})
export class GridExportComponent {
    faDownload = faDownload
    exporting = false
    progress = '0%'
    @Input() grid!: FlexGrid
    @Input() grids!: FlexGrid[]
    @Input() name!: string
    @Input() names!: string[]
    @Input() snakeCaseHeaders = false
    @Input() noNumberFormatting = false
    /** When false, the export button is hidden (e.g. when parent provides its own Export button that opens a modal). Default true. */
    @Input() showButton = true

    /**
     * Export grid to Excel. When grid is provided (e.g. a temporary grid with filtered columns),
     * that grid is exported instead of the bound @Input() grid. onComplete is called when export finishes (e.g. to destroy the temp grid).
     */
    export(grid?: FlexGrid, onComplete?: () => void) {
        this.exporting = true
        const targetGrid = grid ?? this.grid
        if (this.grids == null) {
            const options: any = {
                includeColumnHeaders: true,
                includeStyles: true,
                sheetName: this.name,
            }

            // Add formatItem callback if custom options are enabled
            if (this.snakeCaseHeaders || this.noNumberFormatting) {
                options.formatItem = (args: any) => {
                    this.formatExportItem(args)
                }
            }

            FlexGridXlsxConverter.saveAsync(
                targetGrid,
                options,
                this.name + '.xlsx',
                () => {
                    this.exporting = false
                    this.progress = '0%'
                    onComplete?.()
                },
                undefined,
                progress => (this.progress = progress + '%'),
                true,
            )
        } else {
            let workbook: Workbook

            this.grids.forEach((grid, index) => {
                const options: any = {
                    includeColumnHeaders: true,
                    includeStyles: false,
                }

                // Add formatItem callback if custom options are enabled
                if (this.snakeCaseHeaders || this.noNumberFormatting) {
                    options.formatItem = (args: any) => {
                        this.formatExportItem(args)
                    }
                }

                const sheetData = FlexGridXlsxConverter.saveAsync(grid, options)

                sheetData.sheets[0].name = this.names[index]

                // merging all grid into same workbook/ different sheets
                if (workbook) {
                    workbook.sheets.push(sheetData.sheets[0])
                } else {
                    workbook = sheetData
                }
            })
            workbook!.saveAsync(
                this.name,
                () => {
                    this.exporting = false
                    this.progress = '0%'
                },
                undefined,
                progress => (this.progress = progress + '%'),
            )
        }
    }

    private formatExportItem(args: any) {
        // Handle snake case headers
        if (this.snakeCaseHeaders && args.panel.cellType === CellType.ColumnHeader) {
            const headerText = args.panel.getCellData(args.row, args.col, false) as string
            if (headerText && args.xlsxCell) {
                args.xlsxCell.value = this.toSnakeCase(headerText)
            }
        }

        // Handle number formatting removal
        if (this.noNumberFormatting && args.panel.cellType === CellType.Cell) {
            // Get the raw numeric value (true = get raw value, false = get formatted value)
            const rawValue = args.xlsxCell.value

            // Check if the value is a number
            if (typeof rawValue === 'number' && !isNaN(rawValue) && args.xlsxCell) {
                // Set the raw numeric value
                args.xlsxCell.value = rawValue
                // Remove formatting by setting format to null
                if (args.xlsxCell.style) {
                    args.xlsxCell.style.format = ''
                }
            }
        }
    }

    private toSnakeCase(str: string): string {
        return str
            .replace(/\s+/g, '_') // Replace spaces with underscores
            .replace(/[^\w\-_]/g, '') // Remove special characters except underscores and hyphens
            .replace(/-/g, '_') // Replace hyphens with underscores
            .toLowerCase() // Convert to lowercase
    }
    cancel() {
        FlexGridXlsxConverter.cancelAsync(() => {
            this.exporting = false
            this.progress = '0%'
        })
    }
}
